import React from 'react';

import { PaymentAccepted } from './PaymentAccepted/PaymentAccepted';
import { PaymentDecline } from './PaymentDecline/PaymentDecline';
import { PaymentEnter } from './PaymentEnter/PaymentEnter';
import { PaymentHardError } from './PaymentHardError/PaymentHardError';
import { PaymentPending } from './PaymentPending/PaymentPending';
import { PaymentSoftError } from './PaymentSoftError/PaymentSoftError';
import { PaymentSuccessful } from './PaymentSuccessful/PaymentSuccessful';
import {
  FinanceStatusStatuses,
  PAYMENTS_VIEWS,
  PaymentStatus,
  PaymentViewProps,
  PostSaleOfferApprovals,
  PostSaleOfferState,
} from './Payments.types';

const getPaymentComponentBaseOnView = (view: `${PAYMENTS_VIEWS}`): {
  component: React.FunctionComponent<PaymentViewProps>;
  i18nKey: string;
} => {
  switch (view) {
  case PAYMENTS_VIEWS.ENTER:
    return { component: PaymentEnter, i18nKey: 'revisedOffer' };
  case PAYMENTS_VIEWS.ACCEPT:
    return { component: PaymentAccepted, i18nKey: 'revisedOffer' };
  case PAYMENTS_VIEWS.SUCCESS:
    return { component: PaymentSuccessful, i18nKey: 'sold' };
  case PAYMENTS_VIEWS.DECLINE:
    return { component: PaymentDecline, i18nKey: 'revisedOffer' };
  case PAYMENTS_VIEWS.PENDING:
    return { component: PaymentPending, i18nKey: 'acceptedOffer' };
  case PAYMENTS_VIEWS.SOFT_ERROR:
    return { component: PaymentSoftError, i18nKey: 'acceptedOffer' };
  case PAYMENTS_VIEWS.HARD_ERROR:
    return { component: PaymentHardError, i18nKey: 'acceptedOffer' };
  case null:
  default:
    return { component: () => null, i18nKey: 'revisedOffer' };
  }
};

const getPaymentComponentBaseOnData = (
  postSaleOfferState?: PostSaleOfferState,
  paymentStatus?: PaymentStatus,
  approvals?: PostSaleOfferApprovals[],
): {
  component: React.FunctionComponent<PaymentViewProps>;
  i18nKey: string;
} => {
  const isApprovedBySeller = Boolean(approvals && approvals.find((item: PostSaleOfferApprovals) => item.userType === 'seller'));

  switch (postSaleOfferState) {
  case 'rejected':
    return { component: PaymentHardError, i18nKey: 'acceptedOffer' };
  case 'pending':
    if (isApprovedBySeller) {
      return { component: PaymentPending, i18nKey: 'acceptedOffer' };
    }
    return { component: PaymentEnter, i18nKey: 'revisedOffer' };
  case 'approved':
    switch (paymentStatus) {
    case 'done':
    case 'not_required':
      return { component: PaymentSuccessful, i18nKey: 'sold' };
    case 'failed':
    case 'errored':
      return { component: PaymentHardError, i18nKey: 'acceptedOffer' };
    case 'initial':
    case 'pending':
    default:
      return { component: PaymentPending, i18nKey: 'acceptedOffer' };
    }
  default:
    return { component: () => null, i18nKey: 'revisedOffer' };
  }
};

export interface GetPaymentsCurrentViewArg {
  approvals?: PostSaleOfferApprovals[];
  paymentStatus?: PaymentStatus;
  postSaleOfferState?: PostSaleOfferState;
  view: `${PAYMENTS_VIEWS}` | null;
}

export const getPaymentsCurrentView = ({
  approvals,
  paymentStatus,
  postSaleOfferState,
  view,
}: GetPaymentsCurrentViewArg): {
  component: React.FunctionComponent<PaymentViewProps>;
  i18nKey: string;
} => {
  if (view) {
    return getPaymentComponentBaseOnView(view);
  }
  return getPaymentComponentBaseOnData(postSaleOfferState, paymentStatus, approvals);
};

export const PHONES = {
  CONTACT_US_PAGE_FOR_SELLERS: '02038857079',
  INCORRECT_FINANCE_AMOUNT: '02045780846',
  PAYMENT_ERROR: '02045780846',
};

export const NE_FUNDS_PAID_STATUSES = new Set<FinanceStatusStatuses>(['overpaid', 'paid']);
